import { getActiveDateIntervalName } from 'components/Dashboard/Analytics/createAnalyticsState';
import Preloader from 'components/Dashboard/Analytics/ui/AnalyticsDashboard/Preloader';
import DownloadChartModal from 'components/Dashboard/Analytics/ui/DownloadChartModal/DownloadChartModal';
import { DateFilterButton } from 'components/Dashboard/Analytics/ui/AnalyticsFilters/FiltersBar';
import OverallSentimentChartData from 'components/Dashboard/Analytics/ui/OverallSentiment/OverallSentimentChartData';
import PieOverallSentimentChart from 'components/Dashboard/Analytics/ui/OverallSentiment/PieOverallSentimentChart';
import getUrlParam from 'components/Dashboard/utilities/getUrlParam';
import { store } from 'components/Store';
import dayjs from 'dayjs';
import React, { useContext, useRef, useState } from 'react';

function StakeholderSentimentSnapshotGraph(props) {
  const globalState = useContext(store);
  const { state } = globalState;
  const { keywordsLists } = state;

  const {
    StakeholderInformation,
    sentimentSnapshotState,
    setRightPanelState,
    createSnapshotTextValue,
    sentimentSnapshotAnalyticsState,
  } = props;

  const { sentimentSnapshotAnalytics, setSentimentSnapshotAnalytics } = sentimentSnapshotAnalyticsState ?? {};
  const { name, firstName } = StakeholderInformation;
  const { data, isLoading } = sentimentSnapshotState ?? {};
  const { rawData, value, labels } = data;
  const totalValue = data?.value
    ? data?.value.reduce(
        (previousValue, currentValue) => previousValue + (typeof currentValue === 'number' ? currentValue : 0),
        0
      )
    : 0;
  const emptyMessage = data?.value?.filter((item) => item > 0).length === 0;

  const topicParameter = getUrlParam('topic-id');
  const searchParameter = getUrlParam('search');
  const topic = keywordsLists?.find((item) =>
    topicParameter === 'all' ? item?.id === null : item?.id === parseInt(topicParameter)
  );

  const chartItem = useRef(null);
  const chartSection = useRef(null);
  const downloadButton = useRef(null);

  const [showDownloadModal, setShowDownloadModal] = useState(false);

  const clickFunction = (item) => {
    const index = typeof item === 'string' ? labels?.indexOf(item) : item?.[0]?.index;
    const sentiment = labels[index];
    const value = data?.value[index];
    const dateFilters = [
      {
        field: 'dateTime',
        value: sentimentSnapshotAnalytics?.sentimentDateInterval?.filter?.[0],
        operator: 'date_gte',
      },
      {
        field: 'dateTime',
        value: dayjs().format('YYYY-MM-DD'),
        operator: 'date_lt',
      },
    ];
    setRightPanelState({
      open: true,
      date: null,
      sentiment: sentiment,
      dateFilters,
      noResults: value === 0,
      rawData,
      text: createSnapshotTextValue(item),
      stakeholderPageTitle: `${sentiment} contributions`,
    });
  };

  const modifyFiltersBarFunction = ({ property, newValue }) => {
    const newState =
      property === 'CompleteState' ? newValue : { ...sentimentSnapshotAnalytics, sentimentDateInterval: newValue };
    setSentimentSnapshotAnalytics(newState);
  };

  return (
    <>
      <p className='bg-blue-hovered mt-2 mb-4 rounded-pill border py-2 px-4 paragraph-p2 flex-centered justify-content-between'>
        Sentiment analysis does not always correlate with someone’s stance on an issue{' '}
        <span
          data-tooltip-class-name='pricing-feature-tooltip feed-tooltip billing-tooltip'
          data-tooltip-content={`Sentiment refers to the tone or emotional quality of a statement containing your keywords, and doesn’t necessarily reflect the speaker’s support or opposition to an issue. For example, the sentence 'It's fantastic that animal abuse is in decline' may register as positive sentiment for the keyword 'animal abuse' because the language is positive.`}
          data-tooltip-id={'general-tooltip'}
          className='icon-info ml-2'
        />
      </p>
      {isLoading ? (
        <div style={{ minHeight: '512px' }}>
          <Preloader />
        </div>
      ) : (
        <>
          {!emptyMessage && (
            <div className='pb-4'>
              {labels?.map((section, index) => {
                const titleLowerCase = section.toLowerCase();
                const numberValue = (value[index] * 100) / totalValue;
                const valuePercentage = Math.round((numberValue + Number.EPSILON) * 10) / 10;
                let percentage = valuePercentage % 1 === 0 ? valuePercentage.toFixed(0) : valuePercentage.toFixed(1);
                percentage += '%';
                return (
                  <React.Fragment key={`label-${titleLowerCase}`}>
                    <div className='mb-2 paragraph-p1 main-subtle-text flex-centered align-items-start pr-4'>
                      <span
                        className={`sentiment-bar-dot sentiment-dot specific-right-margin-12 d-inline-block specific-top-margin-7 sentiment-dot-${section?.toLowerCase()}`}
                      />
                      <p className='mb-0 flex-grow-1'>
                        {firstName}
                        {firstName.endsWith('s') ? "'" : "'s"} contributions in relation to this{' '}
                        {searchParameter ? 'keyword' : 'topic'} in the last{' '}
                        {getActiveDateIntervalName(sentimentSnapshotAnalytics?.sentimentDateInterval, 'last year')} were{' '}
                        <span className='font-weight-bold'>
                          {percentage} {section.toLowerCase()}
                        </span>
                      </p>
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
          )}
          <div className='border py-3 rounded'>
            <div className='px-4'>
              <div className='pb-4 justify-content-between flex-centered stakeholder-sentiment-filters-bar'>
                <div className='flex-centered'>
                  <h3 className='title-h5-m-bold my-3 nowrap-item flex-grow-1 sentiment-changes-chart-title'>
                    Sentiment snapshot
                  </h3>
                  <div className='ml-3'>
                    <DateFilterButton
                      analyticsState={sentimentSnapshotAnalytics}
                      modifyFiltersBar={modifyFiltersBarFunction}
                      customLeftValue={0}
                      maxHeightValue={250}
                      activeDateIntervalProp='sentimentDateInterval'
                      /* customOptionsNames={[
                        'Last 7 days',
                        'Last 30 days',
                        'Last 3 months',
                        'Last 6 months',
                        'Last 12 months',
                      ]} */
                      titleModifier={(name) => name?.replace('Last', 'in the last')}
                    />
                  </div>
                </div>

                {!emptyMessage && (
                  <button
                    ref={downloadButton}
                    className='general-button circle-button circle-button-36 border'
                    disabled={isLoading}
                    onClick={() => {
                      downloadButton.current.blur();
                      setShowDownloadModal(true);
                    }}
                    data-tooltip-id={'general-tooltip'}
                    data-tooltip-content={'Download chart'}
                  >
                    <span className='icon-download' />
                  </button>
                )}
              </div>
            </div>
            <div className='pt-1 pb-3' ref={chartSection}>
              <PieOverallSentimentChart
                data={data}
                ref={chartItem}
                clickFunction={clickFunction}
                analyticsState={sentimentSnapshotAnalytics}
              />
            </div>
          </div>
          {!emptyMessage && (
            <div className='stakeholder-sentiment-chart-data specific-top-padding-30 pb-3'>
              <OverallSentimentChartData
                data={data}
                analyticsState={sentimentSnapshotAnalytics}
                chartRef={chartItem}
                activeSections={labels}
                clickFunction={clickFunction}
              />
            </div>
          )}
        </>
      )}

      <DownloadChartModal
        setIsOpen={setShowDownloadModal}
        isOpen={showDownloadModal}
        chartSection={chartSection}
        type={'Sentiment snapshot'}
        data={data}
        chartRef={chartItem}
        analyticsState={sentimentSnapshotAnalytics}
        setChartOnDownload={() => {}}
        initialHeader={`Political sentiment by ${name}: ${searchParameter ?? topic?.name}`}
      />
    </>
  );
}

export default StakeholderSentimentSnapshotGraph;
